import { Link } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import type { BackofficeUser, PremiumToken } from 'api/generated'
import { Button } from 'components/ui/button'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

export interface MembershipCodesColumnsProps {
  onDelete: (id: number) => void
  onOpenUserDialog: (user: BackofficeUser) => void
}

export const membershipCodesColumns = (
  props: MembershipCodesColumnsProps
): ColumnDef<PremiumToken>[] => [
  {
    accessorKey: 'created_at',
    header: 'Data',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
  },
  {
    // The backend returns the redeemable code: random for new codes, salt + id for older ones.
    // A backend deployed before `code` existed omits it: fall back to the old salt + id form.
    accessorKey: 'code',
    header: 'Codice',
    cell: ({ row }) => {
      const code =
        (row.original.code as string | undefined) ?? `2989${row.original.id}`
      return (
        <a
          href={`https://tennistalker.it/diventa-member/${encodeURIComponent(code)}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          {code}
        </a>
      )
    },
  },
  {
    accessorKey: 'user.id',
    header: 'UserID',
    cell: ({ row }) => {
      const user = row.original.user

      if (!user) {
        return '-'
      }

      return (
        <button
          onClick={() => props.onOpenUserDialog(user)}
          className='cursor-pointer border-none bg-transparent p-0'
        >
          {user.id}
        </button>
      )
    },
  },
  {
    accessorKey: 'shopify_order_id',
    header: 'ShopifyID',
    cell: ({ row }) => {
      const shopifyOrderId = row.original.shopify_order_id
      if (shopifyOrderId == null) return '-'
      return (
        <a
          href={`https://tennistalker.myshopify.com/admin/orders/${shopifyOrderId}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          {shopifyOrderId}
        </a>
      )
    },
  },
  {
    accessorKey: 'consumed',
    header: 'Consumato',
    cell: ({ row }) => String(row.original.consumed),
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.original.email ?? ''
      return (
        <div className='flex items-center'>
          {email}
          <Copy
            className='ml-2 cursor-pointer'
            onClick={() => {
              navigator.clipboard.writeText(email)
              toast.success('Email copiata.')
            }}
          />
        </div>
      )
    },
  },
  {
    accessorKey: 'duration',
    header: 'Durata',
  },
  {
    accessorKey: 'id',
    header: 'Azioni',
    cell: ({ row }) => (
      <div className='flex items-center gap-2'>
        {' '}
        {/* Use flex and gap-2 for spacing */}
        <Link
          to='/codici-membership/$id/modifica'
          params={{ id: row.original.id.toString() }}
        >
          Modifica
        </Link>
        <Button
          size='sm'
          variant='destructive'
          onClick={() => {
            if (
              window.confirm('Sei sicuro di voler eliminare questo banner?')
            ) {
              props.onDelete(row.original.id)
            }
          }}
        >
          Elimina
        </Button>
      </div>
    ),
  },
]
